import React, { useState } from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    Input,
    FormText,
    FormGroup,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import Select from 'react-select';
import swal from 'sweetalert';
import { ButtonStyles } from "../../constants/ButtonStyles";
import { saveInvestorConfig } from "../../util/APIUtils";

export default function AddInvestorScreen({
    onBack,
    form,
    onFormChange,
    onSubmit,
    meta,
    formErrors,
    error,
    refreshMeta
}) {
    // State for add new modals
    const [addModal, setAddModal] = useState({ open: false, type: '', label: '', fieldName: '' });
    const [newItemName, setNewItemName] = useState('');
    const [newTransTypeValue, setNewTransTypeValue] = useState('');

    // TransType value options
    const transTypeValueOptions = [
        { value: 'CREDIT', label: 'CREDIT' },
        { value: 'DEBIT', label: 'DEBIT' },
        { value: 'OTHERS', label: 'OTHERS' }
    ];

    // Convert meta data to react-select format with "Add New" option
    const investorOptions = [
        { value: '__ADD_NEW__', label: '+ Add New Investor', isAddNew: true },
        ...(meta && meta.investors ? meta.investors : []).map(inv => ({ value: inv.name, label: inv.name }))
    ];

    const typeOptions = [
        { value: '__ADD_NEW__', label: '+ Add New Transaction Type', isAddNew: true },
        ...(meta && meta.transactionTypes ? meta.transactionTypes : []).map(t => ({ value: t, label: t }))
    ];

    const categoryOptions = [
        { value: '__ADD_NEW__', label: '+ Add New Category', isAddNew: true },
        ...(meta && meta.category ? meta.category : []).map(c => ({ value: c.name, label: c.name }))
    ];

    // Custom styles for "Add New" option
    const customStyles = (hasError) => ({
        control: (base) => ({
            ...base,
            borderRadius: "10px",
            borderColor: hasError ? "#eb3349" : "#e0e0e0",
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
        }),
        option: (base, state) => ({
            ...base,
            fontWeight: state.data.isAddNew ? '600' : 'normal',
            color: state.data.isAddNew ? '#667eea' : base.color,
            backgroundColor: state.data.isAddNew && state.isFocused ? '#f0f0ff' : base.backgroundColor
        })
    });

    // Handle dropdown change with "Add New" detection
    const handleSelectChange = (selectedOption, fieldName, configType, label) => {
        if (selectedOption && selectedOption.value === '__ADD_NEW__') {
            setAddModal({ open: true, type: configType, label, fieldName });
            setNewItemName('');
            setNewTransTypeValue('');
        } else {
            onFormChange({ target: { name: fieldName, value: selectedOption ? selectedOption.value : "" } });
        }
    };

    // Save new config item
    const handleSaveNewItem = () => {
        if (!newItemName.trim()) {
            swal('Please enter a name');
            return;
        }
        if (addModal.type === 'TransType' && !newTransTypeValue) {
            swal('Please select a value type (CREDIT/DEBIT/OTHERS)');
            return;
        }

        const payload = {
            type: addModal.type,
            name: newItemName.trim(),
            value: addModal.type === 'TransType' ? newTransTypeValue : newItemName.trim()
        };

        saveInvestorConfig(payload)
            .then(() => {
                swal('Success', `${addModal.label} added successfully!`, 'success');
                const fieldName = addModal.fieldName;
                const newValue = newItemName.trim();
                setAddModal({ open: false, type: '', label: '', fieldName: '' });
                setNewItemName('');
                setNewTransTypeValue('');
                // Refresh metadata and auto-select the new item
                if (typeof refreshMeta === 'function') {
                    refreshMeta().then(() => {
                        onFormChange({ target: { name: fieldName, value: newValue } });
                    });
                }
            })
            .catch(err => swal('Error', 'Failed to save: ' + (err.message || err), 'error'));
    };

    const closeAddModal = () => {
        setAddModal({ open: false, type: '', label: '', fieldName: '' });
        setNewItemName('');
        setNewTransTypeValue('');
    };

    return (
        <div>
            {/* Add New Modal */}
            <Modal isOpen={addModal.open} toggle={closeAddModal}>
                <ModalHeader toggle={closeAddModal}>
                    <i className="fa fa-plus-circle" style={{ marginRight: "10px", color: "#667eea" }}></i>
                    Add New {addModal.label}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label style={{ fontWeight: "600", color: "#555" }}>Name</Label>
                        <Input
                            type="text"
                            value={newItemName}
                            onChange={(e) => setNewItemName(e.target.value)}
                            placeholder={`Enter ${addModal.label} name`}
                            style={{ borderRadius: "8px" }}
                        />
                    </FormGroup>
                    {addModal.type === 'TransType' && (
                        <FormGroup>
                            <Label style={{ fontWeight: "600", color: "#555" }}>Value Type</Label>
                            <Select
                                value={transTypeValueOptions.find(opt => opt.value === newTransTypeValue) || null}
                                onChange={(opt) => setNewTransTypeValue(opt ? opt.value : '')}
                                options={transTypeValueOptions}
                                placeholder="Select Type (CREDIT/DEBIT/OTHERS)"
                                styles={{ control: (base) => ({ ...base, borderRadius: "10px" }) }}
                            />
                        </FormGroup>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button style={ButtonStyles.primarySm} onClick={handleSaveNewItem}>
                        <i className="fa fa-save" style={{ marginRight: "6px" }}></i> Save
                    </Button>
                    <Button style={ButtonStyles.secondarySm} onClick={closeAddModal}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>

            <Card style={{
                borderRadius: "15px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                border: "none"
            }}>
                <CardHeader style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "15px 15px 0 0",
                    padding: "15px 20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <strong style={{ color: "#fff", fontSize: "18px" }}>
                        <i className="fa fa-plus-circle" style={{ marginRight: "10px" }}></i>
                        Add Investor Transaction
                    </strong>
                    <Button
                        onClick={onBack}
                        style={ButtonStyles.secondary}
                    >
                        <i className="fa fa-arrow-left" style={{ marginRight: "8px" }}></i>
                        Back
                    </Button>
                </CardHeader>
                <CardBody style={{ padding: "15px 20px" }}>
                    <Row style={{ marginBottom: "10px" }}>
                        <Col md="6">
                            <FormGroup style={{ marginBottom: "8px" }}>
                                <Label style={{ fontWeight: "600", color: "#555", marginBottom: "4px", fontSize: "13px" }}>
                                    <i className="fa fa-user" style={{ marginRight: "6px", color: "#667eea" }}></i>
                                    Investor Name
                                </Label>
                                <Select
                                    name="investorName"
                                    value={investorOptions.find(opt => opt.value === form.investorName && !opt.isAddNew) || null}
                                    onChange={(opt) => handleSelectChange(opt, 'investorName', 'INVESTOR', 'Investor')}
                                    options={investorOptions}
                                    placeholder="Select Investor"
                                    isClearable
                                    isSearchable
                                    styles={customStyles(formErrors.investorName)}
                                />
                                {formErrors.investorName && (
                                    <FormText color="danger" style={{ fontWeight: "500" }}>
                                        {formErrors.investorName}
                                    </FormText>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup style={{ marginBottom: "8px" }}>
                                <Label style={{ fontWeight: "600", color: "#555", marginBottom: "4px", fontSize: "13px" }}>
                                    <i className="fa fa-money" style={{ marginRight: "6px", color: "#11998e" }}></i>
                                    Amount
                                </Label>
                                <Input
                                    type="text"
                                    name="transactionAmount"
                                    value={form.transactionAmount || ""}
                                    onChange={onFormChange}
                                    placeholder="Enter amount"
                                    style={{
                                        borderRadius: "8px",
                                        padding: "8px 12px",
                                        border: formErrors.transactionAmount ? "1px solid #eb3349" : "1px solid #e0e0e0",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                                    }}
                                />
                                {formErrors.transactionAmount && (
                                    <FormText color="danger" style={{ fontWeight: "500" }}>
                                        {formErrors.transactionAmount}
                                    </FormText>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row style={{ marginBottom: "10px" }}>
                        <Col md="6">
                            <FormGroup style={{ marginBottom: "8px" }}>
                                <Label style={{ fontWeight: "600", color: "#555", marginBottom: "4px", fontSize: "13px" }}>
                                    <i className="fa fa-calendar" style={{ marginRight: "6px", color: "#f7971e" }}></i>
                                    Transaction Date
                                </Label>
                                <Input
                                    type="date"
                                    name="transactionDate"
                                    value={form.transactionDate || ""}
                                    onChange={onFormChange}
                                    style={{
                                        borderRadius: "8px",
                                        padding: "8px 12px",
                                        border: formErrors.transactionDate ? "1px solid #eb3349" : "1px solid #e0e0e0",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                                    }}
                                />
                                {formErrors.transactionDate && (
                                    <FormText color="danger" style={{ fontWeight: "500" }}>
                                        {formErrors.transactionDate}
                                    </FormText>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup style={{ marginBottom: "8px" }}>
                                <Label style={{ fontWeight: "600", color: "#555", marginBottom: "4px", fontSize: "13px" }}>
                                    <i className="fa fa-exchange" style={{ marginRight: "6px", color: "#764ba2" }}></i>
                                    Transaction Type
                                </Label>
                                <Select
                                    name="type"
                                    value={typeOptions.find(opt => opt.value === form.type && !opt.isAddNew) || null}
                                    onChange={(opt) => handleSelectChange(opt, 'type', 'TransType', 'Transaction Type')}
                                    options={typeOptions}
                                    placeholder="Select Type"
                                    isClearable
                                    isSearchable
                                    styles={customStyles(formErrors.type)}
                                />
                                {formErrors.type && (
                                    <FormText color="danger" style={{ fontWeight: "500" }}>
                                        {formErrors.type}
                                    </FormText>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row style={{ marginBottom: "10px" }}>
                        <Col md="6">
                            <FormGroup style={{ marginBottom: "8px" }}>
                                <Label style={{ fontWeight: "600", color: "#555", marginBottom: "4px", fontSize: "13px" }}>
                                    <i className="fa fa-tags" style={{ marginRight: "6px", color: "#38ef7d" }}></i>
                                    Category
                                </Label>
                                <Select
                                    name="transactionCategory"
                                    value={categoryOptions.find(opt => opt.value === form.transactionCategory && !opt.isAddNew) || null}
                                    onChange={(opt) => handleSelectChange(opt, 'transactionCategory', 'CATEGORY', 'Category')}
                                    options={categoryOptions}
                                    placeholder="Select Category"
                                    isClearable
                                    isSearchable
                                    styles={customStyles(formErrors.transactionCategory)}
                                />
                                {formErrors.transactionCategory && (
                                    <FormText color="danger" style={{ fontWeight: "500" }}>
                                        {formErrors.transactionCategory}
                                    </FormText>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup style={{ marginBottom: "8px" }}>
                                <Label style={{ fontWeight: "600", color: "#555", marginBottom: "4px", fontSize: "13px" }}>
                                    <i className="fa fa-file-text" style={{ marginRight: "6px", color: "#4facfe" }}></i>
                                    Description
                                </Label>
                                <Input
                                    type="textarea"
                                    name="description"
                                    value={form.description || ""}
                                    onChange={onFormChange}
                                    placeholder="Enter description"
                                    rows="2"
                                    style={{
                                        borderRadius: "8px",
                                        padding: "8px 12px",
                                        border: formErrors.description ? "1px solid #eb3349" : "1px solid #e0e0e0",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                        resize: "none"
                                    }}
                                />
                                {formErrors.description && (
                                    <FormText color="danger" style={{ fontWeight: "500" }}>
                                        {formErrors.description}
                                    </FormText>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    {error && (
                        <Row>
                            <Col style={{ textAlign: "center", marginBottom: "15px" }}>
                                <FormText color="danger" style={{ fontWeight: "bold", fontSize: "14px" }}>
                                    {error}
                                </FormText>
                            </Col>
                        </Row>
                    )}

                    <Row style={{ marginTop: "15px" }}>
                        <Col style={{ textAlign: "center" }}>
                            <Button
                                onClick={onSubmit}
                                style={{ ...ButtonStyles.primarySm, marginRight: "15px" }}
                            >
                                <i className="fa fa-save" style={{ marginRight: "8px" }}></i>
                                Save Transaction
                            </Button>
                            <Button
                                onClick={onBack}
                                style={ButtonStyles.dangerSm}
                            >
                                <i className="fa fa-times" style={{ marginRight: "8px" }}></i>
                                Cancel
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        </div>
    );
}



// WEBPACK FOOTER //
// ./src/components/CustomComponents/AddInvestorScreen.js